import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Stack, Paper, Chip } from '@mui/material'
import Page from '../../layout/Page'
import { useLocation, useParams, useSearchParams, useNavigate } from 'react-router-dom'
import RoleContext from '../../auth/RoleContext';
import UserContext from '../../auth/UserContext'
import CacheContext from '../../cache/CacheContext'
import { ROLES } from '../../auth/role';
import { deleteDraftResource, deletePublishedResource, getSiteSettings, getResourceLibraryFileById, getResourceLibraryFiles, getResourceLibraryFilesByTag, getResourceLibraryFilesForIdentity, getResourceLibraryFilesInStatus, publishResourceLibraryDraft } from '../data'
import ResourceCategoryList from './ResourceCategoryList'
import SubmitResourceDialog from './SubmitResourceDialog'
import TabPanel from '../../form/TabPanel'
import ConfirmDialog from '../../form/ConfirmDialog'
import EditResourceDialog from './EditResourceDialog'
import ResourceLibraryCommandBar from './ResourceLibraryCommandBar';
import { SAFETYRESOURCES } from '../../users/data/preferences';
import CategoriesListControls from './ResourceList/CategoriesListControls';
import { getSiteConfig } from '../../community/siteProvider';
import AllResourcesCategoriesList from './AllResourcesCategoriesList';
import { useQuery, useQueryClient } from '@tanstack/react-query';
// import FeatureFlagsContext from '../../config/FeatureFlagsContext';

export default function ResourceLibraryPage(props = {}) {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams({ view: 'all_resources'})
  const urlParams = useParams()
  const navigate = useNavigate()
  const roleDetails = useContext(RoleContext)
  const cache = useContext(CacheContext)
  const [isLoading, setIsLoading] = useState(true)
  const [files, setFiles] = useState({})
  const [searching, setSearching] = useState(false)
  const [categories, setCategories] = useState([])
  const [viewCategories, setViewCategories] = useState(null)
  const [createResourceDialogOpen, setCreateResourceDialogOpen] = useState(false)
  const [editResourceDialogOpen, setEditResourceDialogOpen] = useState(false)
  const userContext = useContext(UserContext)
  const [searchScope, setSearchScope] = useState(null)
  const queryClient = useQueryClient()
  
  // const ff = useContext(FeatureFlagsContext)

  const { pathname } = useLocation()
  
  const siteKey = useMemo(() => {
    return pathname.split('/').filter(Boolean)[0] ?? null
  }, [pathname])
  
  const getCategoriesFromQuery = useCallback((allCategories) => {
    const raw = searchParams.get('categories')
    if (!raw || !allCategories?.length) return null

    const requested = raw
      .split(',')
      .map(c => decodeURIComponent(c).trim())
      .filter(Boolean)

    // Only allow valid categories
    const valid = requested.filter(c => allCategories.includes(c))
    return valid.length ? valid : null
  }, [searchParams])

  const [config, setConfig] = useState(null)
  const [driveConfig, setDriveConfig] = useState(null)

  const TabbedViews = {
    ALL_RESOURCES: 'all_resources',
    MY_FAVORITES: 'my_favorites',
    MY_RESOURCES: 'my_resources',
    SBMTD_RESOURCES: 'submitted_resources',
    RESULTS: 'results'
  }

  const [currentView, setCurrentView] = useState(TabbedViews.ALL_RESOURCES)
  const [viewBump, setViewBump] = useState(0)
  const [resultView, setResultView] = useState(null)


  const getFavorites = async () => {
    const favs = await userContext.getFavorites(userContext.uniqueId, SAFETYRESOURCES)
    cache.setItem(`${SAFETYRESOURCES}Favorites`, favs)
    return favs
  }

  const startLoading = () => {
    setIsLoading(true)
  }

  const stopLoading = () => {
    setIsLoading(false)
  }

  const [currentLocation, setCurrentLocation] = useState(null)

  useEffect(() => {
    (async () => {
      let hasNavigated = false
      if (currentLocation !== null) {
        const {site, sectionId} = currentLocation
        if (siteKey !== site) hasNavigated = true
        if (urlParams?.sectionId !== sectionId) hasNavigated = true
      } else {
        hasNavigated = true
      }
      if (hasNavigated) {
        setCurrentLocation({site: siteKey, sectionId: urlParams?.sectionId ?? undefined})
        const _config = getSiteConfig(siteKey)
        setConfig(_config)

        const _driveConfig = urlParams.sectionId ? _config.libraryDrives.find(s => s.route === urlParams.sectionId) : _config.libraryDrives[0]
        setDriveConfig(_driveConfig)

        //const settings = await getSiteSettings(true, _driveConfig)
        
        await getFavorites()
      }
    })()
  }, [siteKey, urlParams, categories])

  const { data: siteSettings } = useQuery({ 
    queryKey: ['site-settings', driveConfig?.route ],
    queryFn: () => getSiteSettings(true, driveConfig),
    enabled: (driveConfig !== null)
  })

  useEffect(() => {
    if (driveConfig && siteSettings) {
      const cats = siteSettings.resources.categories
      cats.sort((a, b) => a.localeCompare(b))
      setCategories(cats)
      const deepLinked = getCategoriesFromQuery(cats)
      if (deepLinked) {
        setViewCategories(deepLinked)
      } else {
        setViewCategories(cats)
      }
    }
  }, 
  [driveConfig, siteSettings, searchParams])


  const isManager = useCallback(() => {
    const always = [ROLES.ADMIN, ROLES.MFI_ADMIN]
    const managers = config ? [...config.managerRoles, ...always] : always
    return roleDetails.hasRole(managers)
  }, [config])

  const getCurrentView = useCallback(() => {
    const resourceId = searchParams.get('resourceId') ?? null
    let currentView = searchParams.get('view') ?? TabbedViews.ALL_RESOURCES
    if (resourceId) {
      currentView = TabbedViews.RESULTS
    }
    return [currentView, resourceId]
  },[searchParams])

  useEffect(() => {
    const [_currentView, _resourceId] = getCurrentView()
    startLoading()
    if (_currentView === TabbedViews.ALL_RESOURCES) {
      setFiles({})
    } else {
      queryClient.invalidateQueries({queryKey: ['category-files']})
    }
    if (_currentView === TabbedViews.MY_FAVORITES) {
      (async () => {
        setSearching(true)
        const _files = {}
        try {
          const favs = await getFavorites()
          if (favs && favs.length) {
            const results = await getResourceLibraryFiles(favs, undefined, true, driveConfig)
            _files['Favorites'] = results
          }
        } finally {
          setSearching(false)
        }
        setFiles(_files)
      })()
    }

    if (_currentView === TabbedViews.MY_RESOURCES) {
      (async () => {
        setSearching(true)
        const _files = {}
        try {
          
          const requests = [
            getResourceLibraryFilesForIdentity({ email: userContext.email, userName: userContext.userName }, 'Submitted', driveConfig),
            getResourceLibraryFilesForIdentity({ email: userContext.email, userName: userContext.userName }, 'Published', driveConfig),
          ]
          
          const groups = await Promise.all(requests)
          _files['Submitted'] = groups[0]
          _files['Published'] = groups[1]

        } finally {
          setSearching(false)
        }
        setFiles(_files)
      })()
    }

    if (_currentView === TabbedViews.SBMTD_RESOURCES) {
      (async () => {
        const _files = {}
        try {
          _files['Submitted'] = await getResourceLibraryFilesInStatus('DRAFT', driveConfig)
        } finally {
          setSearching(false)
        }
        // console.log('_files', files)
        setFiles(_files)
      })()
    }

    if (_currentView === TabbedViews.RESULTS) {
      const _tagView = searchParams.get('tagged') ?? null

      if (_resourceId) {
        const resource = location.state ? location.state[_resourceId] : undefined
        
        if (resource) {
          const title = resource.fields?.Title
          const fileName = resource.fields?.LinkFilename
          const _files = { [title ?? fileName]: [resource]}
          setResultView(title ?? fileName)
          setFiles(_files)
        } else {
          // search by resourceId
          (async () => {
            setSearching(true)
            try {
              const resource = await getResourceLibraryFileById(_resourceId, driveConfig)
              const title = resource.fields?.Title
              const fileName = resource.fields?.LinkFilename
              setResultView(title ?? fileName)
              const _files = { [title ?? fileName]: [resource]}
              setFiles(_files)
            } catch (error) {
              // console.error(error)
              setFiles(null)
            } finally {
              setSearching(false)
            }
          })()
        }
      } else if (_tagView) {
        (async () => {
          setSearching(true)
          setResultView(_tagView)
          const _files = { [_tagView]: []}
          try {
            const _taggedFiles = await getResourceLibraryFilesByTag(_tagView, driveConfig)

            _files[_tagView] = _taggedFiles.reduce((p, file) => {
              if (file.driveItem.parentReference?.name === 'Published') {
                return [...p, file]
              }
              return p
            }, [])
          } finally {
            setSearching(false)
          }
            
          // console.log('_files', _files)
          setFiles(_files)
        })()
      }
    }
    setCurrentView(_currentView)
    stopLoading()
  }, [categories, searchParams, driveConfig, viewBump])
  
  const [confirmDeleteDialogActive, setConfirmDeleteDialogActive] = useState(false)
  const [confirmDeleteAction, setConfirmDeleteAction] = useState({ resourceId: undefined, listKey: undefined, isPublished: false})

  const findResourceInList = (resourceId, listKey) => {
    const _tagView = searchParams.get('tagged') ?? null
    const [_currentView, _resourceId] = getCurrentView()

    let fileList
    if (_tagView !== null) {
      fileList = files[_tagView]
    } else if (_currentView === TabbedViews.RESULTS) {
      fileList = files[resultView]
    } else if (_currentView === TabbedViews.MY_FAVORITES) {
      fileList = files['Favorites']
    } else if ([TabbedViews.MY_RESOURCES, TabbedViews.SBMTD_RESOURCES].includes(_currentView)) {
      fileList = files[listKey]
    } else {
      fileList = queryClient.getQueryData(['category-files', driveConfig.route, listKey])
          .filter(Boolean).flat()
    }

    const listIndex = fileList.findIndex((f,i) => f.driveItem.id === resourceId)
    const _file = fileList[listIndex]
    return [_file, listIndex]
  }

  const onApproveDraft = useCallback(async (resourceId, listKey) => {
    const [_file] = findResourceInList(resourceId, listKey)
    if (_file?.fields.Status === 'DRAFT') {
      await publishResourceLibraryDraft(resourceId, driveConfig)
    }
    setViewBump(viewBump + 1)
  })

  const onDeleteResource = useCallback(async (resourceId, listKey) => {
    const [_file, listIndex] = findResourceInList(resourceId, listKey)
    if (_file.fields.Status === 'DRAFT') {
      await deleteDraftResource(resourceId, driveConfig)
    } else {
      await deletePublishedResource(resourceId, driveConfig)
    }
    const [ _currentView ] = getCurrentView()
    if (_currentView === TabbedViews.ALL_RESOURCES) {
      queryClient.refetchQueries({queryKey: ['category-files', driveConfig.route, listKey]})
    } else {
      const _files = structuredClone(files)
      _files[listKey].splice(listIndex, 1)
      setFiles(_files)
    }
  })

  const onSelectTag = useCallback((tag) => {
    setSearchParams({tagged: tag, view: TabbedViews.RESULTS})
  })

  const confirmDeleteResource = useCallback((resourceId, listKey) => {
    setConfirmDeleteAction({resourceId, listKey})
    setConfirmDeleteDialogActive(true)
  })

  const [editResourceProperties, setEditResourceProperties] = useState(null)
  const onEditResourceProperties = useCallback((resourceId, listKey) => {
    const [_file] = findResourceInList(resourceId, listKey)
    const fileName = _file.fields?.LinkFilename
    const title = _file.fields?.Title
    const description = _file.driveItem?.description
    const resourceCategory = _file.fields?.QSEPortalResourceCategory
    const resourceTags = _file.fields?.QSEPortalTags
    setEditResourceProperties({
      resourceId,
      category: resourceCategory,
      fileName,
      title,
      description,
      tags: resourceTags?.split(',') || undefined,
    })
    setEditResourceDialogOpen(true)
  }, [files])

  const onEditResourceCloseEvent = useCallback((navToMyResources, fileTouched) => {
    setEditResourceDialogOpen(false)
    setEditResourceProperties(null)
    const [ _currentView ] = getCurrentView()
    if (navToMyResources && _currentView !== TabbedViews.MY_RESOURCES) {
      setSearchParams({view: TabbedViews.MY_RESOURCES})
    } else if (fileTouched) {
      if (_currentView === TabbedViews.ALL_RESOURCES) {
        queryClient.refetchQueries({queryKey: ['category-files', driveConfig.route]})
      } else {
        setViewBump(viewBump + 1)
      }
    }
  }, [driveConfig])

  
  const sortCategories = useCallback((isAscending = true) => {
    let sc = [...viewCategories]
    if (isAscending === true) {
      sc.sort((a, b) => a.localeCompare(b))
    } else if (isAscending === false) {
      sc.sort((a, b) => b.localeCompare(a))
    }
    setViewCategories(sc)
  }, [viewCategories])

  const filterCategories = useCallback((selection) => {
    if (selection.join('') === 'all') {
      setViewCategories(categories)
      const nextSearchParams = new URLSearchParams(searchParams)
      nextSearchParams.delete('categories')
      setSearchParams(nextSearchParams)
    } else {
      setViewCategories(selection)
      const nextSearchParams = new URLSearchParams(searchParams)
      nextSearchParams.set('categories', selection.map(encodeURIComponent).join(','))
      setSearchParams(nextSearchParams)
    }
  }, [categories, searchParams])

  

  const [categoryExpand, setCategoryExpand] = useState(null)
  const tabViews = [
    { 
      key: TabbedViews.ALL_RESOURCES,
      label: 'All Resources', 
      title: 'Categorized lists of approved shared files',
      el: currentView === TabbedViews.ALL_RESOURCES && (
          <AllResourcesCategoriesList 
            categories={categories}
            viewCategories={viewCategories}
            filterCategories={filterCategories}
            sortCategories={sortCategories}
            getCategoriesFromQuery={getCategoriesFromQuery}
            categoryExpand={categoryExpand}
            setCategoryExpand={setCategoryExpand}
            searching={searching}
            onEditResourceProperties={onEditResourceProperties}
            onDeleteDocument={confirmDeleteResource}
            onResourceUploaded={onEditResourceCloseEvent}
            onSelectTag={onSelectTag}
            onSetSearchScope={(scope) => setSearchScope(scope)}
            isManager={isManager}
            driveConfig={driveConfig}
            withQueryClient={queryClient}
          />
      )
    },
    { 
      key: TabbedViews.MY_FAVORITES,
      label: 'Favorites',
      title: 'Files you\'ve favorited',
      el: currentView === TabbedViews.MY_FAVORITES && (
        <Stack gap={1}>
          <CategoriesListControls setCategoryExpand={setCategoryExpand} 
            sortCategories={sortCategories}
            categories={categories}
            filterCategories={filterCategories}
            viewCategories={viewCategories}
            includeFilter={false}
            includeSort={false}
            />
          {/* {categoriesListControls(false, false)} */}
          <ResourceCategoryList key={'favorites'}
            category={'Favorites'}
            files={files?.['Favorites']}
            working={searching}
            aggregatedView={true}
            onEditResourceProperties={onEditResourceProperties}
            onDeleteDocument={confirmDeleteResource}
            onApproveDraft={onApproveDraft}
            onSelectTag={onSelectTag}
            expand={categoryExpand ?? true}
            onSetSearchScope={(scope) => setSearchScope(scope)}
            isManager={isManager()}
            driveConfig={driveConfig}
          />
      </Stack>
      )
    },
    { 
      key: TabbedViews.MY_RESOURCES,
      label: 'My Resources',
      title: 'Files you\'ve submitted',
      el: currentView === TabbedViews.MY_RESOURCES && (
        <Stack gap={1}>
          <CategoriesListControls setCategoryExpand={setCategoryExpand} 
            sortCategories={sortCategories}
            categories={categories}
            filterCategories={filterCategories}
            viewCategories={viewCategories}
            includeFilter={false}
            includeSort={false}
            />
          {/* {categoriesListControls(false, false)} */}
          { ['Submitted', 'Published'].reduce((p, category, idx) => {
            if (files?.[category]?.length > 0) {
              return [...p, <ResourceCategoryList key={idx}
            category={category}
            files={files?.[category]}
            working={searching}
            onEditResourceProperties={onEditResourceProperties}
            onDeleteDocument={confirmDeleteResource}
            onApproveDraft={onApproveDraft}
            onSelectTag={onSelectTag}
            expand={categoryExpand ?? true}
            isManager={isManager()}
            driveConfig={driveConfig}
          />]
            }
            return p
          }, [])}
      </Stack>
      )
    }
  ]
  if (isManager()) {
    tabViews.push(
      { 
        key: TabbedViews.SBMTD_RESOURCES,
        label: 'Submitted Drafts', 
        title: 'Files that have been submitted but need approval to be visible to users',
        el: currentView === TabbedViews.SBMTD_RESOURCES && (
          <Stack gap={1}>
            { (files?.['Submitted']?.length > 0) &&
               <ResourceCategoryList key={'Submitted'}
              category={'Submitted'}
              files={files?.['Submitted']}
              working={searching}
              onEditResourceProperties={onEditResourceProperties}
              onDeleteDocument={confirmDeleteResource}
              onApproveDraft={onApproveDraft}
              suppressTags={true}
              suppressFavorites={true}
              isManager={isManager()}
              driveConfig={driveConfig}
            />
            }
        </Stack>
        )
      }
    )
  }
  if (resultView !== null) {
    tabViews.push({
      key: TabbedViews.RESULTS,
      label: 'Results',
      el: currentView === TabbedViews.RESULTS && (
        <Stack gap={1}>
            <ResourceCategoryList key={resultView}
              category={resultView}
              files={files?.[resultView]}
              working={searching}
              filter={resultView}
              aggregatedView={true}
              onEditResourceProperties={onEditResourceProperties}
              onDeleteDocument={confirmDeleteResource}
              onClearFilter={() => {
                setResultView(null)
                setSearchParams({ view: TabbedViews.ALL_RESOURCES})}
              }
              onSelectTag={onSelectTag}
              isManager={isManager()}
              driveConfig={driveConfig}
            />
          </Stack>
      )
    })
  }

  const onTabChange = (tabIndex) => {
    const viewKey = tabViews[tabIndex].key
    setResultView(null)
    setSearchParams({view: viewKey})
    setCategoryExpand(null)
  }

  const onResourceDialogClose = (navToMyResources, fileTouched = false) => {
    // console.log(navToMyResources, fileTouched)
    setCreateResourceDialogOpen(false)
    const _currentView = getCurrentView()
    if (navToMyResources) {
      if (_currentView === TabbedViews.MY_RESOURCES) {
        setViewBump(viewBump + 1)
      } else {
        setSearchParams({view: TabbedViews.MY_RESOURCES})
      }
    } else if (fileTouched) {
      if (_currentView === TabbedViews.ALL_RESOURCES) {
        queryClient.refetchQueries({queryKey: ['category-files', driveConfig.route]})
      } else {
        setViewBump(viewBump + 1)
      }
    }
  }


  return (
    <>
    <EditResourceDialog
          open={editResourceDialogOpen}
          resourceCategories={categories}
          editResourceProperties={editResourceProperties}
          onClose={onEditResourceCloseEvent}
          isManager={isManager()}
          driveConfig={driveConfig}
    />
    <SubmitResourceDialog
          open={createResourceDialogOpen}
          resourceCategories={categories}
          onClose={onResourceDialogClose}
          isManager={isManager()}
          driveConfig={driveConfig}
    />
    <ConfirmDialog open={confirmDeleteDialogActive}
      hideDialog={() => {setConfirmDeleteAction(undefined); setConfirmDeleteDialogActive(false)}}
      handleConfirm={() => {onDeleteResource(confirmDeleteAction.resourceId, confirmDeleteAction.listKey); setConfirmDeleteDialogActive(false)}}
      title='Are you sure you want to delete?'
      text='This item will be permanently deleted'
      yesLabel='Yes, Delete'
      noLabel='Cancel'
    />
    <Page {...props} breadcrumb={<div><span>{config?.label ?? ''} Resource Library</span></div>} isLoading={isLoading} commandBar={
      <ResourceLibraryCommandBar addRecord={() => setCreateResourceDialogOpen(true)}
        addRecordButtonVariant="contained"
        showAdd={!createResourceDialogOpen}
        addRecordLabel='Submit a Resource' 
        searchScope={searchScope}
        onClearSearchScope={() => setSearchScope(null)}/>
      }>
      <Paper
        sx={{ backgroundColor: 'transparent', display: 'flex', flexDirection: 'column', flexWrap: 'wrap', margin: '0 auto 1em auto', boxShadow: 'none' }}
      >
        { (config?.libraryDrives.length > 1) && (
          <Stack padding={1} gap={1} flexDirection='row' flexWrap='wrap'>
            {config.libraryDrives.filter(d => !!d.route).map(d => {
              if (d.route === driveConfig.route) {
                return <Chip label={d.label ?? d.name} size='medium' key={d.route} />
              } else {
                return <Chip label={d.label ?? d.name} size='medium' component="button" variant="outlined" clickable 
                  onClick={() => {
                    navigate(`/${siteKey}/resource-library/${d.route}`, {})
                  }} key={d.route} 
                  />
              }
            })}
          </Stack>
        )
        }
        <TabPanel panelId='resources' key='resources' tabs={tabViews} tabStyle={{ textTransform: 'none', minHeight: 'unset'}}
          defaultTab={Math.max(tabViews.findIndex( v => v.key === currentView), 0)}
          onTabChange={(idx) => onTabChange(idx)}
        />
      </Paper>
    </Page>
    </>
  )
}